import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { User, Mail, Crown, Calendar, LogOut, Bookmark, Brain, Settings, Gift, Bell } from 'lucide-react';
import WowMeter from '../components/WowMeter';
import PushNotificationToggle from '../components/PushNotificationToggle';

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    navigate('/auth');
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen py-12 px-4 md:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          {/* Avatar */}
          <div className="w-24 h-24 mx-auto mb-6 rounded-full overflow-hidden border-2 border-white/10">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-white/5 flex items-center justify-center text-white/40 text-3xl font-heading">
                {user.name?.charAt(0) || 'U'}
              </div>
            )}
          </div>

          <h1 className="font-heading text-3xl font-bold mb-2" data-testid="profile-name">{user.name}</h1>
          <p className="text-white/50">{user.email}</p>

          {user.is_premium && (
            <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
              <Crown className="w-5 h-5 text-yellow-500" />
              <span className="text-yellow-500 font-mono text-sm uppercase">Premium Member</span>
            </div>
          )}
        </div>

        {/* Theory of Wow Meter */}
        <div className="mb-8">
          <WowMeter />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/bookmarks')}
            className="card-dark h-auto py-6 flex flex-col items-center gap-3 hover:border-cyan-400/50"
            data-testid="goto-bookmarks"
          >
            <Bookmark className="w-8 h-8 text-cyan-400" />
            <span className="font-mono text-xs uppercase tracking-wider">Bookmarks</span>
          </Button>

          <Button
            variant="ghost"
            onClick={() => navigate('/analyzer')}
            className="card-dark h-auto py-6 flex flex-col items-center gap-3 hover:border-cyan-400/50"
            data-testid="goto-analyzer"
          >
            <Brain className="w-8 h-8 text-cyan-400" />
            <span className="font-mono text-xs uppercase tracking-wider">AI Analyzer</span>
          </Button>

          <Button
            variant="ghost"
            onClick={() => navigate('/referral')}
            className="card-dark h-auto py-6 flex flex-col items-center gap-3 hover:border-cyan-400/50"
            data-testid="goto-referral"
          >
            <Gift className="w-8 h-8 text-cyan-400" />
            <span className="font-mono text-xs uppercase tracking-wider">Referrals</span>
          </Button>
        </div>

        {/* Notification Settings */}
        <div className="card-dark p-4 mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-white/40" />
            <span className="text-white/70">Push Notifications</span>
          </div>
          <PushNotificationToggle />
        </div>

        {/* Account Details */}
        <div className="card-dark p-6 mb-8">
          <h2 className="font-heading text-xl font-semibold mb-6">Account Details</h2>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                <User className="w-5 h-5 text-white/40" />
              </div>
              <div>
                <p className="text-white/40 text-sm">Name</p>
                <p className="text-white">{user.name}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                <Mail className="w-5 h-5 text-white/40" />
              </div>
              <div>
                <p className="text-white/40 text-sm">Email</p>
                <p className="text-white">{user.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                <Crown className="w-5 h-5 text-white/40" />
              </div>
              <div>
                <p className="text-white/40 text-sm">Subscription</p>
                <p className="text-white">{user.is_premium ? 'Premium' : 'Free Tier'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade CTA */}
        {!user.is_premium && (
          <div className="card-dark p-6 mb-8 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border-yellow-500/20">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-semibold mb-1">Upgrade to Premium</h3>
                <p className="text-white/50 text-sm">Unlock unlimited AI access and exclusive features</p>
              </div>
              <Button onClick={() => navigate('/subscription')} className="bg-yellow-500 text-black hover:bg-yellow-400" data-testid="upgrade-btn">
                $9.99/mo
              </Button>
            </div>
          </div>
        )}

        {/* Logout */}
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full py-4 text-red-400 hover:text-red-300 hover:bg-red-500/10 flex items-center justify-center gap-2"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}
